import { Dropdown, MultiselectDropdown, OtpModal, RadioButton, Stepper, TextInput } from '@components/common';
import { OTHERS_SPECIFY, QR_KEYS, siteRadioTypeList, SUPERVISOR_ALLOWED_CLASS, VISIT_STATUS } from '@config/constant';
import { useFormik } from 'formik';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
	defaultSiteAddressFormInitialValues,
	productComplaintDescriptionOptions,
	productComplaintOptions,
	veneerOptions,
} from '.';
import { AccordianDetailList } from '@components/endUserDetails/components/moreDetailsForm/accordianDetailList';
import { useUpdateSiteLeadMutation } from '@services/hooks/site-leads/useUpdateSiteLeadMutation';
import { useValidation } from '@hooks/validations';
import useStepperStore from '@store/stepperStore';
import useSiteLeadStore from '@store/siteLeadStore';
import { useAvailableProductsListQuery } from '@services/hooks/site-leads/useAvailableProductsListQuery';
import { useGetVisitQuery } from '@services/hooks/site-leads/useGetVisitQuery';
import { useUpdateSiteVisitMutation } from '@services/hooks/site-leads/useUpdateSiteVisit';
import { useUploadFreshSiteImages } from '@services/hooks/site-leads/uploadFreshSiteImages';
import { ProductImages } from '../siteProducts/productImages';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { useGetSiteLeadQuery } from '@services/hooks/site-leads/useGetSiteLeadQuery';
import { DealersList } from '@components/endUserDetails/components/moreDetailsForm';
import { AvailableProductsList, UpdateSiteLeadData, UpdateSiteVisitData } from '@services/hooks/site-leads';
import { useGetRxDb } from '@hooks/getRxdbData';
import { useDealersQuery } from '@services/hooks/enduser-details/useDealerQuery';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { isNullOrEmpty } from '@components/delaerVisit/components/addEditDealerVisit';
import { DropdownOptions } from '@components/common/formControl/dropdown';
import { useSiteSummaryListing } from '@services/hooks/site-summary-listing';
import { useImageUpload } from '@hooks/useImageUpload';
import { stopActiveRecordingAndUploadPendingSiteVisit } from '@services/siteVisitRecording/uploadPendingSiteVisitRecording';
import { discardActiveSiteVisitRecording } from '@services/siteVisitRecording/siteVisitRecordingEngine';
import { useDealerListRxDb } from '@services/hooks/enduser-details/rxdbHooks/useDealerListRxDb';
import { useDebounce } from '@hooks/useDebounce';
import { Modal } from '@components/common/modal/modal';
import { QrModal } from '@components/dashboard/components/meeting/qrModal';
import { overlayRemoveFadeAction } from '@components/common/sort';
import { ConfirmationModal } from '@components/conductedMeetings/confirmationModal/confirmationModal';
import { SiteLeadConfigurations } from '@components/siteLeads';
import { keyDownEvent, RoleType, toWholeNumber } from '@helper/utils';
import { ProfileData } from '@services/hooks/profile';
import { VisitCompleteQr } from '../visitCompleteQr/visitCompleteQr';
import { useUpdateSiteVisitStatusMutation } from '@services/hooks/site-leads/useUpdateSiteVisitStatus';
import { ToastType, useTosterStore } from '@store/toster';
import { useQrVerificationStore } from '@store/qrVerificationStore';
import { useGeoLocationDetails } from '@hooks/useGeoLocationDetails';
import { useSiteVisitActiveUrlQuery } from '@services/hooks/site-leads/useSiteVisitActiveUrlQuery';
import { useShowPauseReminderOnVisitFormEdit } from '@components/siteLeads/siteVisitRecording/useShowPauseReminderOnVisitFormEdit';
import { useComponentLevelAccessCheck } from '../../../../authGuard/hooks/useComponentLevelAccessCheck';

export const SiteAddress = (): ReactElement => {
	useShowPauseReminderOnVisitFormEdit();
	useSetMainHeader('Add New Site Visit');
	const { setCurrentStep } = useStepperStore();
	const { SiteAddressValidationSchema } = useValidation();
	useDealersQuery();
	useAvailableProductsListQuery();
	const { data: siteLeadData, isFetching: isSiteLeadFetching } = useGetSiteLeadQuery();
	const { result: availableProductsDataList } = useGetRxDb<AvailableProductsList>('products');
	const { result: availableOtherProductsDataList } = useGetRxDb<AvailableProductsList>('other_products');
	const { mutate: updateSiteLead, isPending: isSiteLeadPending } = useUpdateSiteLeadMutation();
	const {
		dealerName,
		ownerDetails,
		siteVisitId,
		siteDetails,
		setKeyBaseValue,
		setVisitSfid,
		hasVisitRecordingConsent,
	} = useSiteLeadStore();
	const { data: siteVisitData, isFetching: isFetchingSiteVisit, error: siteVisitError } = useGetVisitQuery();
	const { mutate: updateSiteVisit, isPending: isSiteVisitPending } = useUpdateSiteVisitMutation();
	const { mutate: uploadFreshSiteImages, isPending: isPutSiteImagePending } = useUploadFreshSiteImages();
	const { refetch: refetchSiteSummaryList } = useSiteSummaryListing();
	const isDisabled = (siteLeadData?.site_visit_count ?? 0) > 1;
	const { handleAddImage } = useImageUpload();
	const [isRecordingUploadPending, setIsRecordingUploadPending] = useState(false);
	const [dealerSearch, setDealerSearch] = useState<string>('');
	const [verifyQrModal, setVerifyQrModal] = useState<boolean>(false);

	const { result: dealerList } = useDealerListRxDb({
		searchParams: [
			{ key: 'dealername_c', value: dealerSearch },
			{ key: 'dealercode_c', value: dealerSearch },
		],
		sortBy: 'dealername_c',
	});
	const [openQrModal, setOpenQrModal] = useState<boolean>(false);
	const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
	const [openOtpModal, setOpenOtpModal] = useState<boolean>(false);
	const [totalSteps, setTotalSteps] = useState<number>(3);
	const { mutate: updateSiteVisitStatus } = useUpdateSiteVisitStatusMutation();
	const { locationDetails } = useGeoLocationDetails();
	const { data: activeUrlData } = useSiteVisitActiveUrlQuery();
	const activeLink = activeUrlData?.data?.[0];
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const role = profile[0]?.usertype_c;
	const [maxProductDemoedLimit, setMaxProductDemoedLimit] = useState<number>(2);
	const [maxProductDiscussedLimit, setMaxProductDiscussedLimit] = useState<number>(2);
	const [maxProductComplaintLimit, setMaxProductComplaintLimit] = useState<number>(2);
	const isKAESiteTypeFields = useComponentLevelAccessCheck('has-site-type-permission');
	const onDropDownSearch = useDebounce((e: React.KeyboardEvent<HTMLInputElement>): void => {
		const element = e.target as HTMLInputElement;
		setDealerSearch(element.value);
	}, 300);

	const formik = useFormik({
		initialValues: defaultSiteAddressFormInitialValues,
		onSubmit: () => {},
		validationSchema: SiteAddressValidationSchema,
		validateOnMount: true,
		validate: (values: typeof defaultSiteAddressFormInitialValues & { hasOpenedActiveLink?: boolean }) => {
			const errors: Record<string, string> = {};

			// Dynamic validation: if backend marks link as mandatory, require user to open it once
			if (activeLink?.mandatory && !values.hasOpenedActiveLink) {
				errors.hasOpenedActiveLink = 'Please open the required link before submitting.';
			}

			return errors;
		},
	});
	const { setToaster } = useTosterStore();
	const isSiteQrVerified = useQrVerificationStore((s) => s.isVerified(QR_KEYS.SITE));

	const handleVerifyModalClose = useCallback((): void => {
		setVerifyQrModal(false);
		overlayRemoveFadeAction(() => {});
	}, []);

	const handleVerifyModalOpen = useCallback((): void => {
		const apiSaysVerified = siteVisitData?.verification_status_c === VISIT_STATUS.VERIFIED;

		if (isSiteQrVerified || apiSaysVerified) {
			setToaster(true, ToastType.success, 'Visit is already verified.');
			return;
		}
		setVerifyQrModal(true);
	}, [isSiteQrVerified, siteVisitData?.verification_status_c, setToaster]);

	const nextStepAfterVisitUpdate = useCallback((): void => {
		if (formik?.values?.isContractorAvailable?.toLowerCase() === 'no' && SiteLeadConfigurations.OTP_FLOW) {
			setOpenOtpModal(true);
			return;
		}
		if (SiteLeadConfigurations.QR_FLOW) {
			setOpenQrModal(true);
			return;
		}
		setCurrentStep(3);
	}, [formik?.values?.isContractorAvailable, setCurrentStep]);

	const onVisitUpdateSuccess = useCallback((): void => {
		refetchSiteSummaryList?.();
		nextStepAfterVisitUpdate();
		setIsRecordingUploadPending(true);
		const finishRecording = hasVisitRecordingConsent
			? stopActiveRecordingAndUploadPendingSiteVisit(true)
			: discardActiveSiteVisitRecording();
		void finishRecording.finally(() => {
			setIsRecordingUploadPending(false);
		});
	}, [refetchSiteSummaryList, nextStepAfterVisitUpdate, hasVisitRecordingConsent]);

	const runImageUploadAndVisitUpdate = useCallback(
		(productImages: File[] | null | undefined, siteVisitPayload: UpdateSiteVisitData): void => {
			uploadFreshSiteImages(
				{ files: productImages ?? [], folder: 'sitelead' },
				{
					onSuccess: (uploadedImages): void => {
						const imageKeys = ['visit_image1', 'visit_image2', 'visit_image3', 'visit_image4', 'visit_image5'];
						imageKeys.forEach((key, index) => {
							const imageUrl = uploadedImages[index]?.normal_url;
							if (imageUrl) {
								siteVisitPayload[key] = imageUrl;
							}
						});
						updateSiteVisit(siteVisitPayload, {
							onSuccess: (data): void => {
								const rawVisitSfid = data?.sfid;
								const visitSfid = rawVisitSfid == null || rawVisitSfid === '' ? '' : String(rawVisitSfid).trim();
								if (visitSfid) {
									setVisitSfid(visitSfid);
								}
								onVisitUpdateSuccess();
							},
							onError: (error): void => {
								setToaster(true, ToastType.error, error?.message ?? 'Failed to update site visit.', 5000);
							},
						});
					},
					onError: (error): void => {
						setToaster(true, ToastType.error, error?.message ?? 'Failed to upload visit images.', 5000);
					},
				}
			);
		},
		[uploadFreshSiteImages, updateSiteVisit, onVisitUpdateSuccess, setToaster, setVisitSfid]
	);

	const uploadRecordingThenProceed = useCallback(
		(productImages: File[] | null | undefined, siteVisitPayload: UpdateSiteVisitData): void => {
			runImageUploadAndVisitUpdate(productImages, siteVisitPayload);
		},
		[runImageUploadAndVisitUpdate]
	);

	const updateSiteProducts = useCallback((): void => {
		const {
			availableProducts,
			productImages,
			availableOtherProductsDemoed,
			availableOtherProductsDiscussed,
			fvGlueUsedSoFar,
			competitionGlueUsedSoFar,
			noOfActiveSites,
			complaintProductsList,
			productComplaintDescription,
			customProductComplaintDescription,
		} = formik.values;
		const siteVisitPayload: UpdateSiteVisitData = {
			product_available_at_site: isNullOrEmpty(availableProducts),
			other_products_demoed_c: isNullOrEmpty(availableOtherProductsDemoed),
			other_products_discussed_c: isNullOrEmpty(availableOtherProductsDiscussed),
			competition_glue_used_so_far: toWholeNumber(competitionGlueUsedSoFar ?? ''),
			fv_glue_used_so_far: toWholeNumber(fvGlueUsedSoFar ?? ''),
			no_of_active_sites: toWholeNumber(noOfActiveSites ?? ''),
			latitude: locationDetails?.latitude ?? null,
			longitude: locationDetails?.longitude ?? null,
			product1_demoed_mng_c: isNullOrEmpty(complaintProductsList),
			reason_for_using_competition_c:
				productComplaintDescription?.[0]?.value === OTHERS_SPECIFY
					? customProductComplaintDescription ?? null
					: isNullOrEmpty(productComplaintDescription) ?? null,
			weather_work_given_to_joinery_c: formik.values.isWeatherWorkGivenToJoinery,
			joinery_name: formik.values.joineryName,
			joinery_phone_number: formik.values.joineryNumber,
			joinery_address: formik.values.joineryAddress,
			weather_work_given_to_factory_c: formik.values.isWorkGivenToFactory,
			factory_name: formik.values.factoryName,
			factory_phone_number: formik.values.factoryNumber,
			factory_address: formik.values.factoryAddress,
		};

		uploadRecordingThenProceed(productImages, siteVisitPayload);
	}, [formik, locationDetails?.latitude, locationDetails?.longitude, uploadRecordingThenProceed]);

	const buildSiteLeadPayload = (values: typeof formik.values): UpdateSiteLeadData => ({
		site_type: !isKAESiteTypeFields ? values.siteType : '',
		dealer_code: values.dealerName.split('-')[0],
		dealer_name: values.dealerName.split('-')[1],
		glue_potential: values.gluePotentialOfSite,
		architect_name: values.architect_details[0].name,
		architect_mobile_no: values.architect_details[0].number,
		owner_name: values.owner_details[0].name,
		owner_mobile_no: values.owner_details[0].number,
		epoxy_adhesive_used_c: values.veneer,
	});

	const updateVisitIfNeeded = useCallback((): void => {
		if (siteVisitData?.id) {
			const updateStatus =
				isSiteQrVerified || siteVisitData?.verification_status_c === VISIT_STATUS?.VERIFIED
					? VISIT_STATUS.VISITED_VERIFIED
					: VISIT_STATUS.VISITED_NOT_VERIFIED;

			updateSiteVisitStatus(
				{
					siteVisitId: String(siteVisitData.id),
					status: updateStatus,
					contractor_latitude_c:
						isSiteQrVerified || siteVisitData?.verification_status_c === VISIT_STATUS?.VERIFIED
							? locationDetails?.latitude ?? null
							: null,
					contractor_longitude_c:
						isSiteQrVerified || siteVisitData?.verification_status_c === VISIT_STATUS?.VERIFIED
							? locationDetails?.longitude ?? null
							: null,
				},
				{
					onError: (error): void => {
						setToaster(true, ToastType.error, error?.message ?? 'Failed to update visit status.', 5000);
					},
				}
			);
			setKeyBaseValue('siteDetails', {
				...(siteDetails || {}),
				verification_status_c: updateStatus,
			});
		}
	}, [
		siteVisitData,
		isSiteQrVerified,
		updateSiteVisitStatus,
		setKeyBaseValue,
		locationDetails?.latitude,
		locationDetails?.longitude,
		siteDetails,
		setToaster,
	]);

	const handleNext = useCallback((): void => {
		const payload = buildSiteLeadPayload(formik.values);
		updateSiteLead(payload, {
			onSuccess: () => {
				updateVisitIfNeeded();
				updateSiteProducts();
			},
			onError: (error): void => {
				setToaster(true, ToastType.error, error?.message ?? 'Failed to update site lead.', 5000);
			},
		});
	}, [formik.values, updateSiteLead, updateVisitIfNeeded, updateSiteProducts, setToaster]);

	const handleImageAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const maxImageLimit = 5;
			const folderName = 'sitelead';
			const maximumFileSize = 20 * 1024 * 1024;
			const result = handleAddImage(event, formik.values.productImages, maximumFileSize, maxImageLimit, folderName);
			if (result?.updatedImages) {
				formik.setFieldValue('productImages', result.updatedImages);
			}
		},
		[formik, handleAddImage]
	);

	const availableProductsList = useMemo((): DropdownOptions[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.prdgroup3_c ?? '',
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const availableComplaintProductsList = useMemo((): DropdownOptions[] => {
		if (availableProductsDataList?.length) {
			return availableProductsDataList.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c,
				value: product?.productgroup3description_c ?? '',
			}));
		}
		return [];
	}, [availableProductsDataList]);

	const dealersList = useMemo((): DealersList[] => {
		if (dealerList?.length) {
			return dealerList.map((d, index) => ({
				id: index,
				value: `${d._data?.dealercode_c} -  ${d._data?.dealername_c}`,
				name: d._data?.dealername_c,
			}));
		}
		return [];
	}, [dealerList]);

	useEffect(() => {
		if (ownerDetails) {
			const details = [{ id: 0, isActive: true, name: ownerDetails.ownerName, number: ownerDetails.ownerNumber }];
			formik.setFieldValue('owner_details', details);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ownerDetails]);

	const combinedSiteData = useMemo(() => {
		return {
			siteVisitData,
			siteLeadData,
			dealerName,
		};
	}, [siteVisitData, siteLeadData, dealerName]);

	const availableOtherProductsDemoedList = useMemo((): DropdownOptions[] => {
		if (!availableOtherProductsDataList?.length || !combinedSiteData) {
			return [];
		}

		const product1DemoedName = combinedSiteData?.siteVisitData?.product1_demoed_name?.toLowerCase();
		const product2DemoedName = combinedSiteData?.siteVisitData?.product2_demoed_name?.toLowerCase();

		return availableOtherProductsDataList
			.filter((product) => {
				const productName = product?.productgroup3description_c?.toLowerCase();
				return productName && productName !== product1DemoedName && productName !== product2DemoedName;
			})
			.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c || '',
				value: product?.prdgroup3_c || '',
			}));
	}, [availableOtherProductsDataList, combinedSiteData]);

	const extractProductName = (productString: string | undefined | null): string | null => {
		if (!productString) return null;

		const firstHyphenIndex = productString.indexOf('-');
		if (firstHyphenIndex === -1) return productString.toLowerCase();

		return productString.substring(firstHyphenIndex + 1).toLowerCase();
	};

	const availableOtherProductsDiscussedList = useMemo((): DropdownOptions[] => {
		if (!availableOtherProductsDataList?.length || !combinedSiteData) {
			return [];
		}

		const product1DiscussedName = extractProductName(combinedSiteData?.siteVisitData?.product1_discussed);
		const productDiscussed2Name = extractProductName(combinedSiteData?.siteVisitData?.product2_discussed);

		return availableOtherProductsDataList
			.filter((product) => {
				const productName = product?.productgroup3description_c?.toLowerCase();
				return productName && productName !== product1DiscussedName && productName !== productDiscussed2Name;
			})
			.map((product, index) => ({
				id: index,
				name: product?.productgroup3description_c || '',
				value: product?.prdgroup3_c || '',
			}));
	}, [availableOtherProductsDataList, combinedSiteData]);

	useEffect(() => {
		formik.resetForm();

		if (combinedSiteData.siteVisitData && Object.keys(combinedSiteData?.siteVisitData).length) {
			const demoedProducts = [
				combinedSiteData?.siteVisitData.product1_demoed_name,
				combinedSiteData?.siteVisitData.product2_demoed_name,
			]
				.filter((product) => product)
				.join(', ');

			const discussedProducts = [
				combinedSiteData?.siteVisitData.product1_discussed,
				combinedSiteData?.siteVisitData.product2_discussed,
			]
				.filter((product) => product)
				.map((product) => {
					if (!product) return product;

					const firstHyphenIndex = product.indexOf('-');
					if (firstHyphenIndex === -1) return product;

					return product.substring(firstHyphenIndex + 1);
				})
				.join(', ');
			formik.setFieldValue('demoedProducts', demoedProducts);
			formik.setFieldValue('discussedProducts', discussedProducts);
		}

		if (combinedSiteData.siteLeadData?.current_stage_of_site_c?.toLowerCase() === 'site visited') {
			const {
				glue_potential,
				dealer_name,
				dealer_code,
				owner_name,
				owner_mobile_no,
				architect_name,
				architect_mobile_no,
				epoxy_adhesive_used_c,
				weather_work_given_to_joinery_c,
				joinery_name,
				joinery_phone_number,
				joinery_address,
				weather_work_given_to_factory_c,
				factory_name,
				factory_phone_number,
				factory_address,
			} = combinedSiteData.siteLeadData;

			formik.setFieldValue('gluePotentialOfSite', glue_potential);
			formik.setFieldValue('veneer', epoxy_adhesive_used_c);
			formik.setFieldValue('dealerName', `${dealer_code} - ${dealer_name}`);
			formik.setFieldValue('owner_details', [{ id: 0, isActive: true, name: owner_name!, number: owner_mobile_no! }]);
			formik.setFieldValue('architect_details', [
				{ id: 0, isActive: true, name: architect_name!, number: architect_mobile_no! },
			]);
			formik.setFieldValue('isWeatherWorkGivenToJoinery', weather_work_given_to_joinery_c ?? '');
			formik.setFieldValue('joineryName', joinery_name ?? '');
			formik.setFieldValue('joineryNumber', joinery_phone_number ?? '');
			formik.setFieldValue('joineryAddress', joinery_address ?? '');
			formik.setFieldValue('isWorkGivenToFactory', weather_work_given_to_factory_c ?? '');
			formik.setFieldValue('factoryName', factory_name ?? '');
			formik.setFieldValue('factoryNumber', factory_phone_number ?? '');
			formik.setFieldValue('factoryAddress', factory_address ?? '');
		}

		if (combinedSiteData.dealerName) {
			formik.setFieldValue('dealerName', combinedSiteData.dealerName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [combinedSiteData]);

	useEffect(() => {
		if (
			role === RoleType.KAE &&
			siteDetails?.contractorclass &&
			SUPERVISOR_ALLOWED_CLASS.includes(siteDetails.contractorclass)
		) {
			setTotalSteps(4);
		} else {
			setTotalSteps(3);
		}
	}, [siteDetails, role]);

	const handleModalClose = useCallback(() => {
		setOpenQrModal(false);
		setConfirmationModal(true);
	}, []);

	const handleConfirmationModalClose = useCallback(() => {
		setOpenQrModal(true);
		setConfirmationModal(false);
	}, []);

	const handleConfirmSubmit = useCallback(() => {
		handleConfirmationModalClose();
		overlayRemoveFadeAction(() => setOpenQrModal(false));
		setCurrentStep(3);
	}, [handleConfirmationModalClose, setCurrentStep]);

	const handleMultiSelectProductDemoChange = (e: DropdownOptions): void => {
		const hasNA = formik.values.availableOtherProductsDemoed.some((v) => v.value === 'NA');
		const isNASelected = e.value === 'NA';

		setMaxProductDemoedLimit(isNASelected && !hasNA ? 1 : 2);
		if (isNASelected) {
			formik.setFieldValue(
				'availableOtherProductsDemoed',
				hasNA ? [] : [{ id: availableOtherProductsDemoedList.length - 1, value: 'NA' }]
			);
		}
	};

	const handleMultiSelectProductDiscussedChange = (e: DropdownOptions): void => {
		const hasNA = formik.values.availableOtherProductsDiscussed.some((v) => v.value === 'NA');
		const isNASelected = e.value === 'NA';

		setMaxProductDiscussedLimit(isNASelected && !hasNA ? 1 : 2);
		if (isNASelected) {
			formik.setFieldValue(
				'availableOtherProductsDiscussed',
				hasNA ? [] : [{ id: availableOtherProductsDiscussedList.length - 1, value: 'NA' }]
			);
		}
	};

	const handleProductComplaintDescriptionChange = (e: DropdownOptions): void => {
		const hasOtherSelected = formik.values.productComplaintDescription.some((v) => v.value === OTHERS_SPECIFY);
		const isOtherSelected = e.value === OTHERS_SPECIFY;

		setMaxProductComplaintLimit(isOtherSelected && !hasOtherSelected ? 1 : 2);
		if (isOtherSelected) {
			formik.setFieldValue('customProductComplaintDescription', '');
			formik.setFieldValue(
				'productComplaintDescription',
				hasOtherSelected ? [] : [{ id: productComplaintDescriptionOptions.length, value: OTHERS_SPECIFY }]
			);
		}
	};

	const handleProductComplaintLast1MonthChange = (e: DropdownOptions): void => {
		if (e.value === 'No') {
			formik.setFieldValue('complaintProductsList', []);
			formik.setFieldValue('productComplaintDescription', []);
		}
	};

	useEffect(() => {
		if (role) {
			formik.setFieldValue('role', role);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [role, combinedSiteData]);

	// When backend sends a mandatory link after initial render, ensure Formik picks it up
	useEffect(() => {
		if (activeLink?.mandatory) {
			formik.validateForm();
		}
	}, [activeLink]);

	return (
		<section className="new-site-section add-site-visit-wrapper">
			<div className="container">
				<Wrapper isLoaderVisible={isSiteLeadFetching || isFetchingSiteVisit} isError={siteVisitError}>
					<form>
						<ul className="new-site-wrapper row">
							{!isKAESiteTypeFields && (
								<li className="d-col d-col-2  form-radio">
									<RadioButton
										name="siteType"
										title="Site Type"
										checked={formik.values.siteType}
										radioOptions={siteRadioTypeList}
										onChange={formik.handleChange}
										isDisabled={isDisabled}
										required
									/>
								</li>
							)}
							<li className="d-col d-col-2 ">
								<Dropdown
									id="dealerName"
									name="dealerName"
									label="Dealer Name"
									onBlur={formik.handleBlur}
									title={formik.values.dealerName ?? ''}
									error={formik.touched.dealerName && formik.errors.dealerName ? formik.errors.dealerName : null}
									options={dealersList}
									setFieldValue={formik.setFieldValue}
									disabled={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.dealer_code &&
											siteLeadData?.dealer_name
										)
									}
									handelKeyDownProp={onDropDownSearch}
									listFetching={true}
									required
								/>
							</li>
							<li className="form-control d-col d-col-2">
								<TextInput
									id="gluePotentialOfSite"
									name="gluePotentialOfSite"
									onBlur={formik.handleBlur}
									value={formik.values.gluePotentialOfSite ?? ''}
									setFieldValue={formik.setFieldValue}
									onKeyDown={keyDownEvent}
									label="Glue Potential of Site"
									error={
										formik.touched.gluePotentialOfSite && formik.errors.gluePotentialOfSite
											? formik.errors.gluePotentialOfSite
											: null
									}
									disabled={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.glue_potential
										)
									}
									required
								/>
							</li>
							<li className="d-col">
								<span className="divider-border"></span>
							</li>

							{formik.values.owner_details.map((owner, index) => (
								<AccordianDetailList
									key={owner.id}
									title="Owner Details"
									accordianName="owner_details"
									accordianListIndex={index}
									formik={formik}
									data={owner}
									isEditing={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.owner_name &&
											siteLeadData?.owner_mobile_no
										)
									}
								/>
							))}
							{formik.values.architect_details.map((architect, index) => (
								<AccordianDetailList
									key={architect.id}
									title="Architect Details"
									accordianName="architect_details"
									accordianListIndex={index}
									formik={formik}
									data={architect}
									isEditing={isDisabled && !!(siteLeadData?.architect_name && siteLeadData?.architect_mobile_no)}
								/>
							))}

							<li className="d-col">
								<span className="divider-border"></span>
							</li>

							<li className="d-col d-col-2 ">
								<Dropdown
									id="isWeatherWorkGivenToJoinery"
									name="isWeatherWorkGivenToJoinery"
									label="Whether work is given to a Joinery?"
									onBlur={formik.handleBlur}
									title={formik.values.isWeatherWorkGivenToJoinery ?? ''}
									error={
										formik.touched.isWeatherWorkGivenToJoinery && formik.errors.isWeatherWorkGivenToJoinery
											? formik.errors.isWeatherWorkGivenToJoinery
											: null
									}
									options={veneerOptions}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>

							{formik.values.isWeatherWorkGivenToJoinery?.toLowerCase() === 'yes' && (
								<>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryName"
											name="joineryName"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryName ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Joinery Name"
											error={formik.touched.joineryName && formik.errors.joineryName ? formik.errors.joineryName : null}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryNumber"
											name="joineryNumber"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryNumber ?? ''}
											setFieldValue={formik.setFieldValue}
											onKeyDown={keyDownEvent}
											maxLength={10}
											label="Joinery Number"
											error={
												formik.touched.joineryNumber && formik.errors.joineryNumber ? formik.errors.joineryNumber : null
											}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="joineryAddress"
											name="joineryAddress"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.joineryAddress ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Joinery Address"
											error={
												formik.touched.joineryAddress && formik.errors.joineryAddress
													? formik.errors.joineryAddress
													: null
											}
											required
										/>
									</li>
								</>
							)}

							{role === RoleType.KAE && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="isWorkGivenToFactory"
										name="isWorkGivenToFactory"
										label="Whether work is given to a Factory?"
										onBlur={formik.handleBlur}
										title={formik.values.isWorkGivenToFactory ?? ''}
										error={
											formik.touched.isWorkGivenToFactory && formik.errors.isWorkGivenToFactory
												? formik.errors.isWorkGivenToFactory
												: null
										}
										options={veneerOptions}
										setFieldValue={formik.setFieldValue}
										required
									/>
								</li>
							)}

							{role === RoleType.KAE && formik.values.isWorkGivenToFactory?.toLowerCase() === 'yes' && (
								<>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryName"
											name="factoryName"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryName ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Factory Name"
											error={formik.touched.factoryName && formik.errors.factoryName ? formik.errors.factoryName : null}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryNumber"
											name="factoryNumber"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryNumber ?? ''}
											setFieldValue={formik.setFieldValue}
											onKeyDown={keyDownEvent}
											maxLength={10}
											label="Factory Number"
											error={
												formik.touched.factoryNumber && formik.errors.factoryNumber ? formik.errors.factoryNumber : null
											}
											required
										/>
									</li>
									<li className="form-control d-col d-col-2">
										<TextInput
											id="factoryAddress"
											name="factoryAddress"
											type="text"
											onBlur={formik.handleBlur}
											value={formik.values.factoryAddress ?? ''}
											setFieldValue={formik.setFieldValue}
											label="Factory Address"
											error={
												formik.touched.factoryAddress && formik.errors.factoryAddress
													? formik.errors.factoryAddress
													: null
											}
											required
										/>
									</li>
								</>
							)}

							<li className="d-col">
								<span className="divider-border"></span>
							</li>

							<li className="form-control d-col d-col-2" title={formik?.values?.demoedProducts ?? ''}>
								<TextInput
									id="demoedProducts"
									name="demoedProducts"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.demoedProducts ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Product Demoed"
									error={
										formik.touched.demoedProducts && formik.errors.demoedProducts ? formik.errors.demoedProducts : null
									}
									isAutoFocus
									disabled
								/>
							</li>
							<li className="form-control d-col d-col-2" title={formik?.values?.discussedProducts ?? ''}>
								<TextInput
									id="discussedProducts"
									name="discussedProducts"
									type="text"
									onBlur={formik.handleBlur}
									value={formik.values.discussedProducts ?? ''}
									setFieldValue={formik.setFieldValue}
									label="Product Discussed"
									error={
										formik.touched.discussedProducts && formik.errors.discussedProducts
											? formik.errors.discussedProducts
											: null
									}
									disabled
								/>
							</li>
							<li className="d-col d-col-2">
								<ProductImages fieldName="productImages" formik={formik} handleAddImage={handleImageAdd} required />
							</li>
							<li className="d-col d-col-2">
								<MultiselectDropdown
									id="availableProducts"
									name="availableProducts"
									label="Product Available at Site"
									className="input-border"
									onBlur={formik.handleBlur}
									values={formik.values.availableProducts}
									options={availableProductsList}
									setFieldValue={formik.setFieldValue}
									maxLimit={3}
									error={
										formik.touched.availableProducts && formik.errors.availableProducts
											? formik.errors.availableProducts.toString()
											: null
									}
								/>
							</li>
							<li className="d-col d-col-2">
								<MultiselectDropdown
									id="availableOtherProductsDemoed"
									name="availableOtherProductsDemoed"
									label="Other Products Demoed"
									className="input-border"
									onBlur={formik.handleBlur}
									values={formik.values.availableOtherProductsDemoed}
									options={availableOtherProductsDemoedList}
									setFieldValue={formik.setFieldValue}
									maxLimit={maxProductDemoedLimit}
									onChange={handleMultiSelectProductDemoChange}
									required
									error={
										formik.touched.availableOtherProductsDemoed && formik.errors.availableOtherProductsDemoed
											? formik.errors.availableOtherProductsDemoed.toString()
											: null
									}
								/>
							</li>
							<li className="d-col d-col-2">
								<MultiselectDropdown
									id="availableOtherProductsDiscussed"
									name="availableOtherProductsDiscussed"
									label="Other Products Discussed"
									className="input-border"
									onBlur={formik.handleBlur}
									values={formik.values.availableOtherProductsDiscussed}
									options={availableOtherProductsDiscussedList}
									setFieldValue={formik.setFieldValue}
									maxLimit={maxProductDiscussedLimit}
									onChange={handleMultiSelectProductDiscussedChange}
									required
									error={
										formik.touched.availableOtherProductsDiscussed && formik.errors.availableOtherProductsDiscussed
											? formik.errors.availableOtherProductsDiscussed.toString()
											: null
									}
								/>
							</li>

							<li className="form-control d-col d-col-2">
								<TextInput
									id="competitionGlueUsedSoFar"
									name="competitionGlueUsedSoFar"
									onBlur={formik.handleBlur}
									value={formik.values.competitionGlueUsedSoFar ?? ''}
									setFieldValue={formik.setFieldValue}
									onKeyDown={keyDownEvent}
									label="Competition Glue Used So Far"
									error={
										formik.touched.competitionGlueUsedSoFar && formik.errors.competitionGlueUsedSoFar
											? formik.errors.competitionGlueUsedSoFar
											: null
									}
									required
								/>
							</li>

							<li className="form-control d-col d-col-2">
								<TextInput
									id="fvGlueUsedSoFar"
									name="fvGlueUsedSoFar"
									onBlur={formik.handleBlur}
									value={formik.values.fvGlueUsedSoFar ?? ''}
									setFieldValue={formik.setFieldValue}
									onKeyDown={keyDownEvent}
									label="FV Glue Used So Far"
									error={
										formik.touched.fvGlueUsedSoFar && formik.errors.fvGlueUsedSoFar
											? formik.errors.fvGlueUsedSoFar
											: null
									}
									required
								/>
							</li>

							<li className="form-control d-col d-col-2">
								<TextInput
									id="noOfActiveSites"
									name="noOfActiveSites"
									maxLength={5}
									onBlur={formik.handleBlur}
									value={formik.values.noOfActiveSites ?? ''}
									setFieldValue={formik.setFieldValue}
									onKeyDown={keyDownEvent}
									label="Number Of Active Sites"
									error={
										formik.touched.noOfActiveSites && formik.errors.noOfActiveSites
											? formik.errors.noOfActiveSites
											: null
									}
									disabled={
										isDisabled &&
										!!(
											siteLeadData?.current_stage_of_site_c?.toLocaleLowerCase() === 'site visited' &&
											siteLeadData?.no_of_active_sites
										)
									}
									required
								/>
							</li>

							{SiteLeadConfigurations.OTP_FLOW && (
								<li className="d-col d-col-2 ">
									<Dropdown
										id="isContractorAvailable"
										name="isContractorAvailable"
										label="Is contractor Present"
										onBlur={formik.handleBlur}
										title={formik.values.isContractorAvailable ?? ''}
										error={
											formik.touched.isContractorAvailable && formik.errors.isContractorAvailable
												? formik.errors.isContractorAvailable
												: null
										}
										options={veneerOptions}
										setFieldValue={formik.setFieldValue}
										required
									/>
								</li>
							)}

							<li className="d-col d-col-2 ">
								<Dropdown
									id="productComplaintLast1Month"
									name="productComplaintLast1Month"
									label="Have you faced any product concern in last one month ?"
									onBlur={formik.handleBlur}
									title={formik.values.productComplaintLast1Month ?? ''}
									error={
										formik.touched.productComplaintLast1Month && formik.errors.productComplaintLast1Month
											? formik.errors.productComplaintLast1Month
											: null
									}
									onChange={handleProductComplaintLast1MonthChange}
									options={productComplaintOptions}
									setFieldValue={formik.setFieldValue}
									required
								/>
							</li>
							{formik.values.productComplaintLast1Month === 'Yes' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="complaintProductsList"
										name="complaintProductsList"
										label="Product Name"
										className="input-border"
										onBlur={formik.handleBlur}
										values={formik.values.complaintProductsList}
										options={availableComplaintProductsList}
										setFieldValue={formik.setFieldValue}
										maxLimit={3}
										required
										error={
											formik.touched.complaintProductsList && formik.errors.complaintProductsList
												? formik.errors.complaintProductsList.toString()
												: null
										}
									/>
								</li>
							)}
							{formik.values.productComplaintLast1Month === 'Yes' && (
								<li className="d-col d-col-2 ">
									<MultiselectDropdown
										id="productComplaintDescription"
										name="productComplaintDescription"
										label="Defect Name / Problem Description"
										className="input-border"
										onBlur={formik.handleBlur}
										values={formik.values.productComplaintDescription}
										options={productComplaintDescriptionOptions}
										onChange={handleProductComplaintDescriptionChange}
										setFieldValue={formik.setFieldValue}
										maxLimit={maxProductComplaintLimit}
										required
										error={
											formik.touched.productComplaintDescription && formik.errors.productComplaintDescription
												? formik.errors.productComplaintDescription.toString()
												: null
										}
									/>
								</li>
							)}

							{formik?.values?.productComplaintDescription?.[0]?.value === OTHERS_SPECIFY && (
								<li className="form-control d-col d-col-2">
									<TextInput
										id="customProductComplaintDescription"
										name="customProductComplaintDescription"
										maxLength={200}
										onBlur={formik.handleBlur}
										type="text"
										value={formik.values.customProductComplaintDescription ?? ''}
										setFieldValue={formik.setFieldValue}
										label="Other Defect Name / Problem Description"
										error={
											formik.touched.customProductComplaintDescription &&
											formik.errors.customProductComplaintDescription
												? formik.errors.customProductComplaintDescription
												: null
										}
										required
									/>
								</li>
							)}

							{activeLink && (
								<>
									<li className="d-col d-col-2">
										<div className="feedback-common-wrapper">
											<span className="h4 refrence-heading">
												Reference Forms
												{activeLink?.mandatory && <span className="bps-required-star">*</span>}
											</span>

											<div className="feedback-content">
												<p>
													Click link to fill form{' '}
													<a
														aria-label="feedback-form"
														href={activeLink.url}
														target="_blank"
														rel="noreferrer"
														onClick={(): void => {
															formik.setFieldValue('hasOpenedActiveLink', true, true);
														}}
													>
														- {activeLink.display_value}
													</a>
												</p>
											</div>
										</div>
									</li>
								</>
							)}
							<>
								<li className="d-col d-col-2">
									<div className="feedback-common-wrapper">
										<span className="h4 refrence-heading"></span>
										<div className="feedback-content">
											<p>
												Click link to fill form{' '}
												<a
													aria-label="feedback-form"
													href="https://forms.office.com/r/tgPBTZPrrp"
													target="_blank"
													rel="noreferrer"
												>
													1. Health Insurance Policy - User Survey
												</a>{' '}
												<span>
													<a
														aria-label="feedback-form"
														href="https://survey.zohopublic.in/zs/aeCfso"
														target="_blank"
														rel="noreferrer"
													>
														2. Use of Cyno HV Adhesive by woodworking contractors
													</a>{' '}
												</span>
												<a
													aria-label="feedback-form"
													href="https://survey.zohopublic.in/zs/KdLE0g"
													target="_blank"
													rel="noreferrer"
												>
													3. Plywood Bending
												</a>
											</p>
										</div>
									</div>
								</li>
							</>
						</ul>
						<div className="verify-site-btn">
							<button
								type="button"
								id="verify-site-button"
								aria-label="verify-site-button"
								className="btn btn-secondary btn-small verify-site-button"
								value="verify-site-button"
								onClick={handleVerifyModalOpen}
							>
								Verify Site Visit
							</button>
						</div>
						{verifyQrModal && (
							<Modal
								modalId="verifyQrModal"
								className="dialog-lg dialog-sm"
								modalOpen={verifyQrModal}
								isEscAllow={false}
								onModalClose={handleVerifyModalClose}
							>
								<VisitCompleteQr siteDetails={siteDetails} verificationKey={QR_KEYS.SITE} />
							</Modal>
						)}
					</form>
				</Wrapper>

				<Stepper
					label="Submit"
					nextHandler={handleNext}
					isNext={
						!formik.isValid ||
						!formik.dirty ||
						isPutSiteImagePending ||
						isRecordingUploadPending ||
						!formik.values.productImages.length
					}
					isPending={isSiteLeadPending || isSiteVisitPending || isPutSiteImagePending || isRecordingUploadPending}
					totalSteps={totalSteps}
				/>
			</div>
			{openQrModal && (
				<Modal
					modalId="siteQrCode"
					className="dialog-sm site-lead-qr-code dialog-bottom"
					modalOpen={openQrModal}
					isEscAllow={false}
					onModalClose={handleModalClose}
					modalTitle={'Scan QR Code To Verify Site'}
				>
					<QrModal qrId={siteVisitId?.toString()} modalClose={handleModalClose} isCloseVisible={false} />
				</Modal>
			)}
			{openOtpModal && (
				<Modal
					modalId="siteOtpCode"
					className="dialog-sm site-lead-otp-code-modal dialog-bottom"
					modalOpen={openOtpModal}
					isEscAllow={false}
					onModalClose={handleModalClose}
					modalTitle={'Validate OTP'}
				>
					<OtpModal
						onClose={() => setConfirmationModal(true)}
						submitButtonLabel={'Generate OTP'}
						hideCancelButton={true}
						onSubmit={() => {
							setOpenOtpModal(false);
							handleConfirmSubmit();
						}}
					/>
				</Modal>
			)}
			{confirmationModal && (
				<Modal
					modalId="confirmationModal"
					className="dialog-sm site-lead-confrim"
					modalOpen={confirmationModal}
					isEscAllow={false}
					onModalClose={handleConfirmationModalClose}
				>
					<ConfirmationModal
						onClose={handleConfirmationModalClose}
						messageClassName={'h3'}
						onSubmit={handleConfirmSubmit}
						message="Are you sure you do not want to verify this site?"
						buttonLabel={'Confirm'}
					/>
				</Modal>
			)}
		</section>
	);
};
